import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/EditOutlined';
import { useEffect, useState } from 'react';
import MediaVideoPlayer from '../MediaVideoPlayer';
import {
  BilingualVideoValue,
  setVideoVersion,
  VideoLanguage,
  videoLanguages,
  videoVersion,
} from './bilingual';
import VideoImport from './index';
import { useVideoImportStyles } from './VideoImport.styles';
import { VideoLanguageTabs } from './VideoModalShared';
import { isYoutubeUrl } from './utils';

const labels: Record<VideoLanguage, string> = { fr: 'Français', en: 'Anglais' };

const VideoBlock = ({
  value,
  onChange,
}: {
  value: BilingualVideoValue;
  onChange: (nextValue: BilingualVideoValue) => void;
}) => {
  const classes = useVideoImportStyles();
  const [language, setLanguage] = useState<VideoLanguage>(videoLanguages(value)[0]);
  const [editing, setEditing] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importLanguage, setImportLanguage] = useState<VideoLanguage>('fr');
  const order = videoLanguages(value);
  const hasVideo = !!(value.video || value.videoEn);
  const version = videoVersion(value, language);
  const source = isYoutubeUrl(value.video || value.videoEn || '') ? 'youtube' : 'upload';
  const defaultLanguage: VideoLanguage = value.video || !value.videoEn ? 'fr' : 'en';

  useEffect(() => {
    setLanguage(defaultLanguage);
    setEditing(false);
  }, [value.video, value.videoEn, defaultLanguage]);

  const save = (next: BilingualVideoValue) => {
    onChange(next);
    setLanguage(videoLanguages(next)[0]);
    setEditing(false);
  };

  if (!hasVideo) {
    return (
      <>
        <button type="button" className={classes.videoImportBtn} onClick={() => setImportOpen(true)}>
          Importer une vidéo
        </button>
        <VideoImport
          bilingual={true}
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onSubmit={save}
        />
      </>
    );
  }

  return (
    <div>
      <VideoLanguageTabs
        languages={order}
        language={language}
        label="Versions de la vidéo"
        missing={{
          fr: !value.video,
          en: !value.videoEn,
        }}
        onChange={lang => {
          if (!videoVersion(value, lang).video) {
            setImportLanguage(lang);
            setImportOpen(true);
            return;
          }
          setLanguage(lang);
          setEditing(false);
        }}
      />
      {editing ? (
        <VideoImport
          key={`${language}-${editing}`}
          bilingual={true}
          inline={true}
          open={true}
          languages={[language]}
          initialLanguage={language}
          lockedSource={source}
          initialValue={value}
          submitLabel="Enregistrer"
          onSubmit={save}
          onClose={() => {
            setLanguage(order[0]);
            setEditing(false);
          }}
        />
      ) : (
        <>
          <MediaVideoPlayer
            videoUrl={version.video}
            autoPlayVideo={version.autoPlayVideo}
            muteVideo={version.muteVideo}
          />
          <div className={classes.videoBlockActions}>
            <IconButton
              aria-label={`Modifier la vidéo ${labels[language]}`}
              onClick={() => setEditing(true)}
              style={{ background: '#000A8C', color: 'white' }}>
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label={`Supprimer la vidéo ${labels[language]}`}
              onClick={() =>
                save(
                  setVideoVersion(value, language, {
                    video: '',
                    autoPlayVideo: 0,
                    muteVideo: 0,
                  })
                )
              }
              style={{ background: '#E63C3C', color: 'white' }}>
              <DeleteIcon />
            </IconButton>
          </div>
        </>
      )}
      <VideoImport
        bilingual={true}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSubmit={save}
        initialValue={value}
        initialLanguage={importLanguage}
        lockedSource={source}
        languages={order}
        submitLabel="Enregistrer"
      />
    </div>
  );
};

export default VideoBlock;
